// Integer division with an explicit rounding mode. All money math that can produce a
// fraction goes through here, so rounding is always a visible decision.

export type RoundingMode = 'HALF_EVEN' | 'HALF_UP' | 'FLOOR' | 'CEIL' | 'TRUNC';

const abs = (x: bigint): bigint => (x < 0n ? -x : x);

/**
 * Returns numerator / denominator rounded with `mode`.
 * HALF_UP rounds halves away from zero; HALF_EVEN is banker's rounding.
 */
export function divideRounded(numerator: bigint, denominator: bigint, mode: RoundingMode): bigint {
  if (denominator === 0n) throw new RangeError('Division by zero');
  if (denominator < 0n) {
    numerator = -numerator;
    denominator = -denominator;
  }
  const quotient = numerator / denominator; // truncates toward zero
  const remainder = numerator % denominator; // same sign as numerator
  if (remainder === 0n) return quotient;

  const negative = numerator < 0n;
  switch (mode) {
    case 'TRUNC':
      return quotient;
    case 'FLOOR':
      return negative ? quotient - 1n : quotient;
    case 'CEIL':
      return negative ? quotient : quotient + 1n;
    case 'HALF_UP':
    case 'HALF_EVEN': {
      const twice = abs(remainder) * 2n;
      const awayFromZero = negative ? quotient - 1n : quotient + 1n;
      if (twice > denominator) return awayFromZero;
      if (twice < denominator) return quotient;
      if (mode === 'HALF_UP') return awayFromZero;
      return quotient % 2n === 0n ? quotient : awayFromZero;
    }
  }
}
